import { SelectReservationOrNowModal } from "components/organisms/HalfModal/SelectReservationOrNowModal";
import { useBlockUserModal } from "components/organisms/modal/BlockUserModal";
import { orderState } from "lib/recoil/atoms";
import { useModal } from "mui-modal-provider";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import KanteiRestartNotifyRepository from "repositories/kantei_restart_notify";
import { Fortune, TelFortune } from "shared/types";
import TalkIcon from "shared/assets/icons/talk.svg";
import TelephoneIcon from "shared/assets/icons/telephone.svg";
import { Button } from "shared/components/atoms/Button";
import { Loading } from "shared/components/templates/Loading";
import { useErrorMessage } from "shared/hooks/useErrorMessage";
import tw from "twin.macro";
import { Paths } from "utils/routes";
import { menuHelper } from "../utils/menuHelper";
import { useCallbackWithAuth } from "features/shared/hooks/useCallbackWithAuth";
import { useFeatureFlags } from "features/shared/hooks/useFeatureFlags";
import { useMaintenanceModal } from "shared/features/Menu/hooks/useMaintenanceModal";
import { useOnlyFirstTimeModal } from "components/organisms/modal/OnlyFirstTimeModal";
import { useMe } from "repositories/hooks";
import { RestrictedTelFortuneModal } from "./RestrictedTelFortuneModal";

const buttonExtensionCss = {
  canOrder: tw`bg-gradient-theme-to-l text-white`,
  canNoticeSubscribe: tw`bg-white! text-theme! border border-solid border-theme`,
  noticeSubscribed: tw`bg-secondary! text-white`,
  paused: tw`bg-secondary! text-white`,
};

const statusDictionary = {
  canOrder: "canOrder",
  canNoticeSubscribe: "canNoticeSubscribe",
  noticeSubscribed: "noticeSubscribed",
  paused: "paused",
} as const;

const buttonLabel = {
  canOrder: "鑑定に進む",
  canNoticeSubscribe: "受付再開通知を受け取る",
  noticeSubscribed: "受付再開通知設定済み",
  paused: "受付一時停止中",
};

export interface OrderButtonProps {
  menu: Fortune | TelFortune | undefined;
  onSuccessSubscribeNotification?: () => void;
  className?: string;
}

/**
 * IDEA:
 *  - カスタムフック使ってロジック切り出しても良いかも
 */
export const OrderButton = ({
  menu,
  onSuccessSubscribeNotification,
  className,
}: OrderButtonProps) => {
  const [submitting, setSubmitting] = useState(false);

  const { me } = useMe();

  const { push } = useRouter();
  const [recoilState, setRecoilState] = useRecoilState(orderState);
  const { showModal } = useModal();

  const isChatMenu = menu?.type === "chat";
  const { canOrder } = menuHelper(menu);
  const isCanOrder = canOrder();

  const { isTelFortuneDisabled } = useFeatureFlags();
  const { showModal: showMaintenaceModal } = useMaintenanceModal();

  const status = useMemo(() => {
    if (isCanOrder) return statusDictionary?.canOrder;
    if (!menu?.isPublished) return statusDictionary.paused;
    if (isChatMenu && !menu?.isSetNotify)
      return statusDictionary.canNoticeSubscribe;
    if (isChatMenu && menu?.isSetNotify)
      return statusDictionary.noticeSubscribed;
    return statusDictionary.paused;
  }, [isCanOrder, isChatMenu, menu]);

  const buttonIcon = isChatMenu ? (
    <TalkIcon
      tw="mr-1.5"
      width={18.7}
      height={17.5}
      viewBox="0, 0, 9.35, 9.5"
    />
  ) : (
    <TelephoneIcon
      tw="mr-1.5"
      width={17.89}
      height={17.83}
      viewBox="0, 0, 8.8, 9.5"
    />
  );

  const goToOrderPage = async () => {
    if (isChatMenu) {
      setRecoilState({ ...recoilState, _type: menu?.type, menuId: menu?.id });
      await push(Paths.order.chat.profile(menu.id));
    } else if (!isChatMenu) {
      if (me.restrictedTelFortune) {
        showModal(RestrictedTelFortuneModal);
        return;
      }

      const modal = showModal(SelectReservationOrNowModal, {
        onSubmit: async (fromNow: boolean) => {
          if (fromNow && isTelFortuneDisabled) {
            showMaintenaceModal();
            return;
          } else {
            try {
              setRecoilState({
                ...recoilState,
                _type: menu?.type,
                menuId: menu?.id,
                fromNow,
              });
              await push(Paths.order.tel.profile(menu?.id));
            } catch (error) {
              console.log("error");
            } finally {
              modal.destroy();
            }
          }
        },
        fortuneTeller: menu?.fortuneTeller,
      });
    }
  };

  const { handleAxiosError } = useErrorMessage();

  const subscribeNotification = async () => {
    setSubmitting(true);
    try {
      await KanteiRestartNotifyRepository.post(menu?.id);
      if (onSuccessSubscribeNotification) onSuccessSubscribeNotification();
    } catch (error) {
      handleAxiosError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const { showModal: showBlockUserModal } = useBlockUserModal();
  const { showModal: showOnlyFirstTimeModal } = useOnlyFirstTimeModal();

  const onClickFooterButton = useCallbackWithAuth(() => {
    if (menu?.isBlocked) {
      showBlockUserModal();
    } else if (isCanOrder) {
      if (menu?.isOnlyFirstTime && menu?.isRepeater) {
        showOnlyFirstTimeModal({
          fortuneTellerId: menu?.fortuneTeller?.id,
          fortuneTellerName: menu?.fortuneTeller?.name,
        });
      } else {
        goToOrderPage();
      }
    } else if (!menu?.isSetNotify) {
      subscribeNotification();
    } else {
      // TODO: 何か出す？
    }
  });

  const disabled =
    status === statusDictionary.paused ||
    status === statusDictionary.noticeSubscribed ||
    submitting;

  return (
    <>
      {submitting && <Loading loading />}
      <Button
        tw="h-12! rounded-md! px-1! min-w-[178px]! font-base"
        css={buttonExtensionCss[status]}
        className={className}
        onClick={onClickFooterButton}
        disabled={disabled}
      >
        {status === statusDictionary.canOrder && buttonIcon}
        <span tw="text-sm tracking-[.41px]">{buttonLabel[status]}</span>
      </Button>
    </>
  );
};
